import { Edit05, Trash02 } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";
import type { AdminFormI18nPaths } from "../../admin-form-editor-i18n-paths";
import type { FormRule } from "../../types/form-rule-types";
import { FormRuleToggle } from "./form-rule-toggle";

export interface FormRuleCardProps {
	rule: FormRule;
	paths: AdminFormI18nPaths;
	onEdit: (id: string) => void;
	onDelete: (id: string) => void;
	onToggleEnabled: (id: string, enabled: boolean) => void;
	/** When true, edit/delete affordances are hidden and the toggle is disabled. */
	isReadOnly?: boolean;
}

/**
 * Single-row presentation of a form rule with name, status badge, condition/action
 * counts, an enable/disable switch, and edit/delete buttons. Pure presenter — emits
 * intent via props.
 */
export function FormRuleCard({
	rule,
	paths,
	onEdit,
	onDelete,
	onToggleEnabled,
	isReadOnly,
}: FormRuleCardProps) {
	const { t } = useAppTranslation();

	const conditionCount = rule.conditions.length;
	const actionCount = rule.actions.length;

	return (
		<li
			className={cx(
				"flex items-start justify-between gap-4 rounded-lg border border-border-secondary bg-background-primary p-4 transition-colors",
				rule.enabled ? "" : "bg-bg-quaternary/30 opacity-75",
			)}
			data-testid={`form-rule-card-${rule.id}`}
		>
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-center gap-2">
					<span className="truncate text-sm font-semibold text-text-primary">
						{rule.name}
					</span>
					<Badge
						color={rule.enabled ? "success" : "gray"}
						size="sm"
						className={rule.enabled ? "" : "opacity-80"}
					>
						{rule.enabled
							? t(paths.formRules.statusEnabled)
							: t(paths.formRules.statusDisabled)}
					</Badge>
				</div>
				{rule.description ? (
					<p
						className="mt-1 line-clamp-2 text-sm text-text-tertiary"
						title={rule.description}
					>
						{rule.description}
					</p>
				) : null}
				<div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-text-tertiary">
					<span>
						{t(paths.formRules.conditionCount, { count: conditionCount })}
					</span>
					<span aria-hidden>•</span>
					<span>{t(paths.formRules.actionCount, { count: actionCount })}</span>
				</div>
			</div>

			<div className="flex shrink-0 items-center gap-2">
				<FormRuleToggle
					isSelected={rule.enabled ?? true}
					onChange={(value) => {
						onToggleEnabled(rule.id, value);
					}}
					aria-label={t(paths.formRules.toggleEnable)}
					isDisabled={isReadOnly}
				/>
				{isReadOnly ? null : (
					<>
						<Button
							size="sm"
							color="tertiary"
							iconLeading={Edit05}
							aria-label={t(paths.formRules.editRule)}
							onClick={() => {
								onEdit(rule.id);
							}}
						/>
						<Button
							size="sm"
							color="tertiary-destructive"
							iconLeading={Trash02}
							aria-label={t(paths.formRules.deleteRule)}
							onClick={() => {
								onDelete(rule.id);
							}}
						/>
					</>
				)}
			</div>
		</li>
	);
}
